/**
 * Funding History Tab Component
 *
 * Displays funding fee history with:
 * - Aggregated funding fee summary
 * - Individual funding fee events (from income_history)
 * - Running total calculation
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Paper, Grid, Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SyncIcon from '@mui/icons-material/Sync';

import type { StrategyInfo, IncomeHistory } from '../../../types/strategy';
import { traderTheme as theme } from '../traderDashboardTheme';

interface FundingHistoryTabProps {
  strategy: StrategyInfo;
}

function formatDate(timestamp: number): string {
  if (!timestamp) return '-';
  const date = new Date(timestamp);
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatShortDate(timestamp: number): string {
  if (!timestamp) return '-';
  const date = new Date(timestamp);
  return date.toLocaleString('ko-KR', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatUsdt(value: number | null | undefined, decimals = 4): string {
  if (value == null) return '-';
  const sign = value >= 0 ? '+' : '';
  return `${sign}$${value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}

function getIncomeTypeLabel(type: string, t: (key: string) => string): string {
  // Handle both PascalCase and UPPER_CASE formats
  const normalizedType = type?.toUpperCase?.() || '';
  switch (normalizedType) {
    case 'FUNDINGFEE':
    case 'FUNDING_FEE':
      return t('traderDashboard.strategyDetail.fundingHistory.incomeTypes.fundingFee');
    case 'COMMISSION':
      return t('traderDashboard.strategyDetail.fundingHistory.incomeTypes.commission');
    case 'REALIZEDPNL':
    case 'REALIZED_PNL':
      return t('traderDashboard.strategyDetail.fundingHistory.incomeTypes.realizedPnl');
    case 'TRANSFER':
      return t('traderDashboard.strategyDetail.fundingHistory.incomeTypes.transfer');
    default:
      return type;
  }
}

function getIncomeTypeColor(type: string): string {
  // Handle both PascalCase and UPPER_CASE formats
  const normalizedType = type?.toUpperCase?.() || '';
  switch (normalizedType) {
    case 'FUNDINGFEE':
    case 'FUNDING_FEE':
      return theme.accent;
    case 'COMMISSION':
      return theme.warning;
    case 'REALIZEDPNL':
    case 'REALIZED_PNL':
      return theme.price.futures;
    default:
      return theme.text.secondary;
  }
}

interface IncomeRowProps {
  income: IncomeHistory;
  runningTotal: number;
  t: (key: string) => string;
}

const IncomeRow: React.FC<IncomeRowProps> = ({ income, runningTotal, t }) => {
  const incomeColor = income.income >= 0 ? theme.profit : theme.loss;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        py: 1.5,
        px: 2,
        borderBottom: `1px solid ${theme.border}`,
        '&:hover': {
          backgroundColor: theme.bg.hover,
        },
      }}
    >
      {/* Time */}
      <Box sx={{ width: '18%' }}>
        <Typography variant="caption" sx={{ color: theme.text.secondary, fontSize: '0.7rem' }}>
          {formatShortDate(income.time)}
        </Typography>
      </Box>

      {/* Type */}
      <Box sx={{ width: '15%' }}>
        <Chip
          label={getIncomeTypeLabel(income.income_type, t)}
          size="small"
          sx={{
            backgroundColor: `${getIncomeTypeColor(income.income_type)}20`,
            color: getIncomeTypeColor(income.income_type),
            fontSize: '0.65rem',
            height: 18,
            fontWeight: 500,
          }}
        />
      </Box>

      {/* Symbol */}
      <Box sx={{ width: '12%' }}>
        <Typography variant="caption" sx={{ color: theme.text.primary, fontSize: '0.75rem' }}>
          {income.symbol}
        </Typography>
      </Box>

      {/* Income */}
      <Box sx={{ width: '15%', textAlign: 'right' }}>
        <Typography
          variant="body2"
          sx={{
            color: incomeColor,
            fontFamily: 'monospace',
            fontWeight: 500,
            fontSize: '0.8rem',
          }}
        >
          {formatUsdt(income.income)}
        </Typography>
      </Box>

      {/* Asset */}
      <Box sx={{ width: '10%', textAlign: 'center' }}>
        <Typography variant="caption" sx={{ color: theme.text.secondary, fontSize: '0.75rem' }}>
          {income.asset}
        </Typography>
      </Box>

      {/* Running Total */}
      <Box sx={{ width: '15%', textAlign: 'right' }}>
        <Typography
          variant="body2"
          sx={{
            color: runningTotal >= 0 ? theme.profit : theme.loss,
            fontFamily: 'monospace',
            fontWeight: 500,
            fontSize: '0.8rem',
          }}
        >
          {formatUsdt(runningTotal)}
        </Typography>
      </Box>

      {/* Info */}
      <Box sx={{ width: '15%', textAlign: 'right' }}>
        <Typography
          variant="caption"
          sx={{
            color: theme.text.muted,
            fontSize: '0.65rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {income.info || '-'}
        </Typography>
      </Box>
    </Box>
  );
};

export const FundingHistoryTab: React.FC<FundingHistoryTabProps> = ({ strategy }) => {
  const { t } = useTranslation();
  const ctx = strategy.execution_context;

  // Get income history (funding fees only or all types)
  // Handle both camelCase (from JSON) and snake_case (from TypeScript type) field names
  const incomeHistory = useMemo(() => {
    const history = ctx.pnl_snapshot?.fetched?.income_history || [];
    // Normalize the data to handle both field naming conventions
    return [...history].map((h: any) => ({
      ...h,
      // Normalize income_type: handle both camelCase and snake_case, and both FUNDING_FEE and FundingFee
      income_type: h.income_type || h.incomeType,
      tran_id: h.tran_id || h.tranId,
      trade_id: h.trade_id || h.tradeId,
    })).sort((a, b) => b.time - a.time);
  }, [ctx.pnl_snapshot]);

  // Filter funding fees only - handle both FUNDING_FEE and FundingFee
  const fundingFees = useMemo(() => {
    return incomeHistory.filter((h) =>
      h.income_type === 'FundingFee' ||
      h.income_type === 'FUNDING_FEE'
    );
  }, [incomeHistory]);

  // Calculate running totals (in reverse order for display)
  const historyWithRunningTotal = useMemo(() => {
    // Calculate cumulative from oldest to newest
    const sortedOldestFirst = [...fundingFees].sort((a, b) => a.time - b.time);
    let runningTotal = 0;
    const totals = new Map<number, number>();

    sortedOldestFirst.forEach((h) => {
      runningTotal += h.income;
      totals.set(h.time, runningTotal);
    });

    return fundingFees.map((h) => ({
      income: h,
      runningTotal: totals.get(h.time) || 0,
    }));
  }, [fundingFees]);

  // Summary statistics
  const summary = useMemo(() => {
    const totalFunding = fundingFees.reduce((sum, h) => sum + h.income, 0);
    const positiveFunding = fundingFees.filter((h) => h.income > 0);
    const negativeFunding = fundingFees.filter((h) => h.income < 0);

    const totalReceived = positiveFunding.reduce((sum, h) => sum + h.income, 0);
    const totalPaid = Math.abs(negativeFunding.reduce((sum, h) => sum + h.income, 0));

    const firstEvent = fundingFees.length > 0 ? Math.min(...fundingFees.map((h) => h.time)) : 0;
    const lastEvent = fundingFees.length > 0 ? Math.max(...fundingFees.map((h) => h.time)) : 0;

    return {
      totalFunding,
      totalReceived,
      totalPaid,
      eventCount: fundingFees.length,
      receivedCount: positiveFunding.length,
      paidCount: negativeFunding.length,
      firstEvent,
      lastEvent,
      synchronized: ctx.funding_fee?.synchronized ?? false,
    };
  }, [fundingFees, ctx.funding_fee]);

  const hasFundingData = fundingFees.length > 0 || ctx.funding_fee;

  if (!hasFundingData) {
    return (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="body1" sx={{ color: theme.text.secondary }}>
          {t('traderDashboard.strategyDetail.fundingHistory.noData')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Summary Cards */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="subtitle2"
          sx={{ color: theme.text.primary, fontWeight: 600, mb: 1.5 }}
        >
          {t('traderDashboard.strategyDetail.fundingHistory.summary')}
        </Typography>
        <Paper
          sx={{
            p: 2,
            backgroundColor: theme.bg.secondary,
            border: `1px solid ${theme.border}`,
          }}
        >
          <Grid container spacing={3}>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" sx={{ color: theme.text.secondary, display: 'block' }}>
                {t('traderDashboard.strategyDetail.fundingHistory.totalFunding')}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: summary.totalFunding >= 0 ? theme.profit : theme.loss,
                  fontFamily: 'monospace',
                  fontWeight: 600,
                }}
              >
                {formatUsdt(summary.totalFunding, 2)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" sx={{ color: theme.text.secondary, display: 'block' }}>
                {t('traderDashboard.strategyDetail.fundingHistory.received')}
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: theme.profit, fontFamily: 'monospace', fontWeight: 500 }}
              >
                +${summary.totalReceived?.toFixed(4) ?? '0.0000'}
              </Typography>
              <Typography variant="caption" sx={{ color: theme.text.muted, fontSize: '0.65rem' }}>
                {summary.receivedCount} {t('traderDashboard.strategyDetail.overview.events')}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" sx={{ color: theme.text.secondary, display: 'block' }}>
                {t('traderDashboard.strategyDetail.fundingHistory.paid')}
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: theme.loss, fontFamily: 'monospace', fontWeight: 500 }}
              >
                -${summary.totalPaid?.toFixed(4) ?? '0.0000'}
              </Typography>
              <Typography variant="caption" sx={{ color: theme.text.muted, fontSize: '0.65rem' }}>
                {summary.paidCount} {t('traderDashboard.strategyDetail.overview.events')}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" sx={{ color: theme.text.secondary, display: 'block' }}>
                {t('traderDashboard.strategyDetail.fundingHistory.status')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                {summary.synchronized ? (
                  <>
                    <CheckCircleIcon sx={{ color: theme.profit, fontSize: 18 }} />
                    <Typography variant="body2" sx={{ color: theme.profit }}>
                      {t('traderDashboard.strategyDetail.overview.synchronized')}
                    </Typography>
                  </>
                ) : (
                  <>
                    <SyncIcon sx={{ color: theme.warning, fontSize: 18 }} />
                    <Typography variant="body2" sx={{ color: theme.warning }}>
                      {t('traderDashboard.strategyDetail.overview.syncing')}
                    </Typography>
                  </>
                )}
              </Box>
            </Grid>
          </Grid>

          {/* Date range */}
          {summary.firstEvent > 0 && (
            <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.border}` }}>
              <Typography variant="caption" sx={{ color: theme.text.muted }}>
                {t('traderDashboard.strategyDetail.fundingHistory.dateRange')}: {formatDate(summary.firstEvent)} → {formatDate(summary.lastEvent)}
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Funding Events Table */}
      <Box>
        <Typography
          variant="subtitle2"
          sx={{ color: theme.text.primary, fontWeight: 600, mb: 1.5 }}
        >
          {t('traderDashboard.strategyDetail.fundingHistory.events')} ({fundingFees.length})
        </Typography>

        <Paper
          sx={{
            backgroundColor: theme.bg.secondary,
            border: `1px solid ${theme.border}`,
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              py: 1,
              px: 2,
              backgroundColor: theme.bg.primary,
              borderBottom: `2px solid ${theme.border}`,
            }}
          >
            <Typography variant="caption" sx={{ width: '18%', color: theme.text.primary, fontWeight: 600, fontSize: '0.75rem' }}>
              {t('traderDashboard.strategyDetail.orders.time')}
            </Typography>
            <Typography variant="caption" sx={{ width: '15%', color: theme.text.primary, fontWeight: 600, fontSize: '0.75rem' }}>
              {t('traderDashboard.strategyDetail.fundingHistory.type')}
            </Typography>
            <Typography variant="caption" sx={{ width: '12%', color: theme.text.primary, fontWeight: 600, fontSize: '0.75rem' }}>
              {t('traderDashboard.strategyDetail.orders.symbol')}
            </Typography>
            <Typography variant="caption" sx={{ width: '15%', color: theme.text.primary, fontWeight: 600, fontSize: '0.75rem', textAlign: 'right' }}>
              {t('traderDashboard.strategyDetail.fundingHistory.income')}
            </Typography>
            <Typography variant="caption" sx={{ width: '10%', color: theme.text.primary, fontWeight: 600, fontSize: '0.75rem', textAlign: 'center' }}>
              {t('traderDashboard.strategyDetail.fundingHistory.asset')}
            </Typography>
            <Typography variant="caption" sx={{ width: '15%', color: theme.text.primary, fontWeight: 600, fontSize: '0.75rem', textAlign: 'right' }}>
              {t('traderDashboard.strategyDetail.fundingHistory.runningTotal')}
            </Typography>
            <Typography variant="caption" sx={{ width: '15%', color: theme.text.primary, fontWeight: 600, fontSize: '0.75rem', textAlign: 'right' }}>
              {t('traderDashboard.strategyDetail.fundingHistory.info')}
            </Typography>
          </Box>

          {/* Rows */}
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {historyWithRunningTotal.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: theme.text.secondary }}>
                  {t('traderDashboard.strategyDetail.fundingHistory.noEvents')}
                </Typography>
              </Box>
            ) : (
              historyWithRunningTotal.map((item, index) => (
                <IncomeRow
                  key={`${item.income.tran_id}-${index}`}
                  income={item.income}
                  runningTotal={item.runningTotal}
                  t={t}
                />
              ))
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default FundingHistoryTab;
